import { ServiceWithPricing } from '@/util/types/serverTypes';

/**
 * Creates charge objects from services with pricing information.
 * 
 * This function transforms ServiceWithPricing objects (which include discount
 * tracking and pricing details) into charge objects ready for the backend.
 * 
 * Charge objects are used to:
 * - Track individual line items on quotes/inspections
 * - Store discount attribution (bundle vs code)
 * - Preserve audit trail (original prices, modifiers, discounts)
 * - Support price overrides
 * 
 * @param services - Array of services with calculated pricing and discount information
 * @returns Array of charge objects ready for backend submission
 * 
 * @example
 * ```typescript
 * const services = calculateServicePrices(...);
 * const charges = createChargeObjects(services);
 * 
 * await callServerAction({
 *   url: 'quote/with-charges',
 *   method: 'post',
 *   data: { quote: quoteData, charges }
 * });
 * ```
 */
export function createChargeObjects(services: ServiceWithPricing[]) {
  return services.map((service) => {
    // Bundle discount is the sum of:
    // - bundleDiscountAmount: discount from being in a bundle
    // - primaryDiscountAmount: additional discount for being the primary service
    const bundleDisc =
      (service.bundleDiscountAmount || 0) +
      (service.primaryDiscountAmount || 0);
    
    // Code discount is separate from bundle discounts
    const codeDisc = service.codeDiscountAmount || 0;

    return {
      // Core identification
      _serviceId: service._id,
      type: 'Charge' as const,
      name: service.name,
      isPrimary: service.isPrimary,

      // Discount attribution - only include IDs when discount is actually applied
      _discountCodeId:
        codeDisc > 0 ? service.appliedDiscountCodeId : undefined,
      _bundleId: bundleDisc > 0 ? service.appliedBundleId : undefined,

      // Pricing audit trail
      originalBasePrice: service.originalBasePrice,
      modifiersAmount: service.modifiersTotal,

      // Discount amounts
      bundleDiscountAmount: bundleDisc,
      codeDiscountAmount: codeDisc,
      discountAmount: bundleDisc + codeDisc,

      // Final pricing
      tax: service.taxAmount,
      preTaxAmount: service.preTaxAmount,
      amount: service.priceOverride ?? service.totalPrice ?? 0,

      // Include existing charge ID for updates (conditional spread)
      ...(service.chargeId && { _id: service.chargeId }),
    };
  });
}

/**
 * Type for the charge objects returned by createChargeObjects.
 * This matches the structure expected by the backend /quote/with-charges endpoint.
 */
export type ChargeObject = ReturnType<typeof createChargeObjects>[number];








